import { FooterComponents, FooterComponentType } from '@repo/types';

/**
 * Client-side mirror of the API's `resolveFooterComponents` defaults (SC-1117) —
 * matches today's hardcoded footer props exactly so a tenant with no stored config (or
 * one whose config failed to load) sees no visual change. Used as a defensive fallback
 * by both the public `footer.tsx` and the admin `FooterComponentsPage`/`View` whenever
 * `useNavigationsQuery`'s data, or its `footerComponents` field, is missing/undefined —
 * distinct from the API's own defend-in-depth defaulting, since a network error or a
 * tenant-scoped auth failure never reaches the API's resolver at all.
 *
 * `SponsorsGrid` intentionally omits `columns` — callers fall back to their own existing
 * responsive default (4 on mobile/tablet, 6 on desktop) whenever `columns` isn't
 * explicitly present in the resolved config, exactly as before this feature shipped.
 */
export const FOOTER_COMPONENT_DEFAULTS: FooterComponents = {
  [FooterComponentType.SponsorsGrid]: {
    enabled: true,
    config: {
      title: 'Our Trusted Partners',
      showImage: true,
      showName: false,
      showDescription: false,
      showAllSponsors: true,
    },
  },
  [FooterComponentType.NewsletterSubscribe]: {
    enabled: true,
    config: {
      title: 'Stay Updated',
    },
  },
  [FooterComponentType.SocialNetworks]: {
    enabled: true,
    config: {},
  },
};

/**
 * Resolves a single footer component's entry from a (possibly missing/undefined)
 * `FooterComponents` map, falling back to `FOOTER_COMPONENT_DEFAULTS` per-key — mirrors
 * the API's `resolveFooterComponents` per-key resolution on the client.
 */
export function resolveFooterComponentEntry(footerComponents: FooterComponents | undefined, type: FooterComponentType) {
  return footerComponents?.[type] ?? FOOTER_COMPONENT_DEFAULTS[type]!;
}
